import React, { useMemo } from 'react';
import { Chip } from '@mui/material';
import ProductSelectionsTable from './ProductSelectionsTable';
import { getSelectionLifecycleStatusMeta } from '../utils/status';

const resolveRowParam = (value, row) => row || value?.row || null;
const resolveValueParam = (value, row) => (row ? value : value?.value);

const buildStatusChip = (value) => {
  const meta = getSelectionLifecycleStatusMeta(value);
  const color = meta.color && meta.color !== 'default' ? meta.color : 'default';
  const variant = color === 'default' ? 'outlined' : 'filled';
  return <Chip size="small" label={meta.label || 'Unknown'} color={color} variant={variant} />;
};

const parseDate = (raw) => {
  if (!raw) return null;
  const dt = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
};

const StatusChangesTable = ({
  rows,
  isMaximized,
  onToggleMaximize,
  onNavigateToDealer,
  userEmail,
  onOpenDetail,
  enableInternalScroll = false,
}) => {
  const columnsTransform = useMemo(
    () => (columns) => {
      const orderedFields = [
        'changedAt',
        'bac',
        'brandsDisplay',
        'previousStatus',
        'newStatus',
        'programCode',
        'programName',
        'serviceItemName',
      ];
      const seen = new Set();
      const byField = new Map((columns || []).map((col) => [col.field, col]));
      const head = orderedFields
        .map((field) => byField.get(field))
        .filter(Boolean)
        .map((col) => {
          seen.add(col.field);
          return col;
        });
      const rest = (columns || []).filter((col) => col?.field && !seen.has(col.field));
      return [...head, ...rest];
    },
    []
  );

  const additionalColumns = useMemo(
    () => [
      {
        field: 'changedAt',
        headerName: 'Changed At',
        width: 220,
        type: 'string',
        valueGetter: (value, row) => {
          const rawRow = resolveRowParam(value, row);
          const rawVal = resolveValueParam(value, row);
          const dt = parseDate(rawRow?.changedAt ?? rawVal);
          return dt ? dt.toISOString() : '';
        },
        valueFormatter: (params) => {
          const dt = parseDate(params?.value);
          return dt ? dt.toLocaleString() : '';
        },
        renderCell: (params) => {
          const dt = parseDate(params?.value);
          return dt ? dt.toLocaleString() : '';
        },
        sortComparator: (v1, v2) => {
          const left = parseDate(v1)?.getTime?.() ?? null;
          const right = parseDate(v2)?.getTime?.() ?? null;
          if (left === right) return 0;
          if (left == null) return -1;
          if (right == null) return 1;
          return left - right;
        },
      },
      {
        field: 'previousStatus',
        headerName: 'Previous Status',
        width: 200,
        renderCell: (params) => buildStatusChip(params?.row?.previousStatus ?? params?.value),
        valueGetter: (value, row) => {
          const rawRow = resolveRowParam(value, row);
          const rawVal = resolveValueParam(value, row);
          return rawRow?.previousStatus ?? rawVal ?? '';
        },
      },
      {
        field: 'newStatus',
        headerName: 'New Status',
        width: 200,
        renderCell: (params) => buildStatusChip(params?.row?.newStatus ?? params?.value),
        valueGetter: (value, row) => {
          const rawRow = resolveRowParam(value, row);
          const rawVal = resolveValueParam(value, row);
          return rawRow?.newStatus ?? rawVal ?? '';
        },
      },
    ],
    []
  );

  return (
    <ProductSelectionsTable
      rows={rows}
      isMaximized={isMaximized}
      onToggleMaximize={onToggleMaximize}
      onNavigateToDealer={onNavigateToDealer}
      userEmail={userEmail}
      onOpenDetail={onOpenDetail}
      enableInternalScroll={enableInternalScroll}
      additionalColumns={additionalColumns}
      additionalColumnsPlacement="end"
      columnsTransform={columnsTransform}
    />
  );
};

export default StatusChangesTable;

